// @ts-nocheck
import { useState, useEffect } from "react"
import { graphql, Slice } from "gatsby"
import { Box, Stack } from "@mui/material"
import { get, sampleSize } from "lodash"
import { useDispatch, addNode, removeNode, useSelector } from "@ggs/store"
import Layout from "../components/layout"
import { Hero3, ArticleBody, DynamicSubNav, SuggestedTrips, FC, ContactCTA } from "../components"
import SEOHead from "../components/head"
import { useStaffData, useTravelGuideData, useDynamicSubNav, useTripsData } from "../local/hooks"
import { mapWishlistData, mapArticleTeaser } from "../helpers/data-mappers"

const TravelGuideView: FC = (props) => {
  const guide = get(props, "data.nodeTravelGuide", {})
  const dispatch = useDispatch()
  const wishlist = useSelector((state) => state.views.wishlist)
  const likedArticleGuides = wishlist?.guide && wishlist?.guide.map((article) => article.nid)
  const likedTrips = wishlist?.trip && wishlist?.trip.map((trip) => trip.nid)
  const { getRandomStaff } = useStaffData()
  const { getNextTravelGuideByNodeId } = useTravelGuideData()
  const { getTripsData } = useTripsData()
  const userCountry = useSelector((state) => state.settings.country)
  const [contactCtaStaff, setContactCtaStaff] = useState(null)
  const [isArticleLiked, setIsArticleLiked] = useState(
    (likedArticleGuides && likedArticleGuides.includes(guide.external_id)) || false
  )
  const [nextArticle, setNextArticle] = useState(null)
  const [suggestedTrips, setSuggestedTrips] = useState(null)
  const externalId = guide?.external_id
  const travelGuideCountry = get(guide, "country[0]", null)
  const travelGuideDestination = get(travelGuideCountry, "destinations[0]", null)
  const refDestinationId = get(travelGuideCountry, "destinations[0].external_id", "")

  // Resolve Solr data.
  useEffect(() => {
    getRandomStaff().then((data) => {
      setContactCtaStaff(data)
    })
    getNextTravelGuideByNodeId(externalId).then((data) => {
      setNextArticle(data)
    })
  }, [])

  useEffect(() => {
    getTripsData(refDestinationId).then((data) => {
      const filteredData = mapWishlistData(likedTrips, data)
      setSuggestedTrips({
        title: travelGuideCountry?.suggested_trips?.title || "",
        subtitle: travelGuideCountry?.suggested_trips?.subtitle || "",
        link: { title: "All Trips", uri: "/trips" },
        items: sampleSize(filteredData, 9),
      })
    })
  }, [userCountry])

  const subNav = useDynamicSubNav({
    title: travelGuideCountry?.title || "Overview",
    uri: travelGuideCountry?.slug,
    nid: travelGuideCountry?.external_id,
  })

  if (!guide) {
    return <Box>{t("Travel Guide not found")}</Box>
  }

  const addToWishList = (selectedEntity) => {
    if (selectedEntity && selectedEntity.contentType === "trip") {
      const filteredState = mapArticleTeaser(suggestedTrips.items, selectedEntity, true)
      setSuggestedTrips({
        ...suggestedTrips,
        items: filteredState,
      })
    }
    if (!selectedEntity) {
      setIsArticleLiked(true)
    }
    dispatch(
      addNode({
        type: selectedEntity ? selectedEntity.contentType : "guide",
        entity: {
          contentType: selectedEntity ? selectedEntity.contentType : "guide",
          nid: selectedEntity ? selectedEntity.nid : externalId,
        },
        idPropertyKey: "nid",
      })
    )
  }

  const removeFromWishList = (selectedEntity) => {
    if (selectedEntity && selectedEntity.contentType === "trip") {
      const filteredState = mapArticleTeaser(suggestedTrips.items, selectedEntity, false)
      setSuggestedTrips({
        ...suggestedTrips,
        items: filteredState,
      })
    }
    if (!selectedEntity) {
      setIsArticleLiked(false)
    }
    dispatch(
      removeNode({
        type: selectedEntity ? selectedEntity.contentType : "guide",
        findPredicate: {
          contentType: selectedEntity ? selectedEntity.contentType : "guide",
          nid: selectedEntity ? selectedEntity.nid : externalId,
        },
      })
    )
  }

  const articleBodyData = {
    description: guide?.body,
    nextItem: nextArticle,
  }

  const getBreadcrumbs = () => {
    const breadcrumbs = [
      {
        title: "Home",
        uri: "/",
      },
      {
        title: "Destinations",
        uri: "/destinations",
      },
    ]

    if (guide?.country?.[0]?.destinations?.[0]) {
      breadcrumbs.push({
        title: guide?.country?.[0]?.destinations?.[0]?.title,
        uri: guide?.country?.[0]?.destinations?.[0]?.slug,
      })
    }

    if (guide?.country) {
      breadcrumbs.push({
        title: guide?.country?.[0]?.title,
        uri: guide?.country?.[0]?.slug,
      })
      breadcrumbs.push({
        title: "Travel Guide",
        uri: `${guide?.country?.[0]?.slug}/travel-guides`,
      })
    }

    breadcrumbs.push({
      title: guide?.title,
    })

    return breadcrumbs
  }

  const contactCtaStatic = {
    title: "Tailor-made travel.",
    subtitle: "Destination Specialists",
    description:
      "Turn your travel dreams into a reality with Goway. Our customized vacations take travellers to all corners of the world and showcase our incredible planet.",
    link: { title: "Meet the team", uri: "/our-experts" },
  }

  return (
    <>
      {ComposeHead(props)}
      <Layout
        hero={
          <Hero3
            title={guide?.title}
            description={guide?.subtitle}
            tags={[{ title: "Travel Guide" }]}
            image={guide?.hero_image}
            location={guide?.country[0]?.title}
            path={getBreadcrumbs()}
            onLike={addToWishList}
            onUnlike={removeFromWishList}
            isArticleLiked={isArticleLiked}
          />
        }
      >
        {subNav && <DynamicSubNav links={subNav} color="yellow" />}
        <ArticleBody layout="guide" {...articleBodyData} />
        {suggestedTrips?.items?.length && (
          <SuggestedTrips
            {...suggestedTrips}
            onLike={addToWishList}
            onUnlike={removeFromWishList}
          />
        )}
        <ContactCTA {...contactCtaStatic} staff={contactCtaStaff} />
        <Slice alias="preFooterStyle1" />
      </Layout>
    </>
  )
}

export default TravelGuideView

export const ComposeHead = (props) => {
  const { nodeTravelGuide } = props.data
  const metatag = get(nodeTravelGuide, "metatag.items")
  const url = get(nodeTravelGuide, "slug")
  const image = get(nodeTravelGuide, "hero_image.variants[0].url")
  return <SEOHead metas={metatag} url={url} image={image} />
}

export const query = graphql`
  query TravelGuideQuery($id: String!) {
    nodeTravelGuide(id: { eq: $id }) {
      id
      slug
      title
      subtitle
      external_id
      body
      metatag {
        items {
          content
          name
        }
      }
      travel_guide_category {
        title
      }
      hero_image {
        variants {
          alt
          url
          height
          style
          title
          width
        }
      }
      country {
        title
        id
        external_id
        slug
        destinations {
          title
          slug
          external_id
        }
        suggested_trips {
          title
          subtitle
        }
      }
      user {
        ...UserData
      }
    }
  }
`
